// @flow
import * as React from 'react';
import { PhotoCarousel } from '@ta/common.photo-carousel';
import { MountedClientOnly } from '@ta/common.client';
import { localize } from '@ta/common.i18n';
import Logging from '@ta/common.logging';
import { HeadProperties, Link, RouteConsumer, useRouteState } from '@ta/common.routing';
import { ModalView } from '@ta/overlays.modal';
import { BrandedHeader } from '@ta/modal.headers';
import type { Photo } from '@ta/common.photo-carousel';
import type { ViewportCategory } from '@ta/common.state';
import type { SectionObject, SectionOverflowItemType } from '@ta/social.section-overflow';

import SectionModal from './SectionModal';
import SectionModalHeader from './SectionModalHeader';
import styles from './FeedSectionPhotoCarousel.less';

export type FeedSectionPhotoCarouselMetaData = {|
  id: any,
  caption: ?string,
  socialReferences: any,
  socialStatistics: any,
  createdDate: any,
  orderedLocations: any,
  __typename: any,
  numPhotos: number,
  route: any,
|};

export type PhotoWithMetaData = {|
  publishedDate?: any,
  uploadDate?: any,
  __typename: any,
  socialReferences: any,
  socialStatistics: any,
  location: any,
  caption: string,
  route: any,
  userProfile: any,
  ...Photo,
|};

export type UIProps = {|
  photos: Array<PhotoWithMetaData>,
  /** Any additional actions that need to be triggered when the photo carousel is clicked */
  onCarouselClick?: (SyntheticMouseEvent<HTMLElement>) => void,
  actor: any,
  actorForUrl?: any,
  member: any,
  deleteObject?: any,
  untagObject?: any,
  metadata: FeedSectionPhotoCarouselMetaData,
  viewportCategory?: ViewportCategory,
  /** Any additional content to show up on the right hand side */
  additionalModalContent?: React.Node,
  /** This is the social statistics bar to show up above the social action */
  socialStatisticsBar: ?React.Node,
  /** This is the social action bar to show up on the right hand side rail */
  socialActionBar: ?React.Node,
  /** This is the social object that the overflow menu will be making changes to */
  overflowMenuObject: SectionObject,
  overflowMenuObjectForType?: { [SectionOverflowItemType]: SectionObject },
  includeRoutedModal: boolean,
|};

type Props = {|
  ...UIProps,
  currentlyDisplayedIndex: number,
  onPhotoChange?: (number, ?SyntheticEvent<HTMLDivElement>) => void,
|};

const THRESHOLD = 0.1;

// Returns the title for open graph when looking at a photo modal
const getTitleForOpenGraph = (location: any, actor: any): ?string => {
  const isAllOGTitleData =
    location?.name != null && location?.isGeo != null && actor?.displayName != null;
  let titleForOpenGraph;
  if (isAllOGTitleData) {
    // Handles a case when location is PoI, the title would be <<POI>> | <<Parent geo>> | Photo by xxx eg Caffe Nemo | Cambridge, Massachssets| Photo by xxx
    if (location.isGeo === false && location.parent.additionalNames.long != null) {
      titleForOpenGraph = localize('social_opengraph_photo_title', {
        locationName: location.name,
        additionalLocationName: location.parent.additionalNames.long,
        userName: actor.displayName,
      });
    }
    // Handles a case when location is Geo, and of type where itself or its parent is continent or bigger the title would be <<its name>> | Photo by xxx eg Asia | Photo by xxx
    else if (location.placetype == null || location.parent.placetype == null) {
      titleForOpenGraph = localize('social_opengraph_photo_title_short', {
        locationName: location.additionalNames.long,
        userName: actor.displayName,
      });
    }
    // Handles all other cases when location is Geo, the title would be <<its name>>,<<Parents name>> |  Photo by xxx eg Cambridge, Massachussets | Photo by xxx
    else if (location?.name != null && location?.parent?.name != null) {
      titleForOpenGraph = localize('social_opengraph_photo_title_short', {
        locationName: `${location.name}, ${location.parent.name}`,
        userName: actor.displayName,
      });
    }
  }

  return titleForOpenGraph;
};

// Returns all the open graph info for a modal of a photo
export const getOpenGraphInfoFromPhoto = (photo: any) => {
  const largestIndex = photo.photoSizes.length - 1;
  return {
    type: 'image.gallery',
    title: getTitleForOpenGraph(photo.location, photo.userProfile) || '',
    description: photo.caption != null ? `${photo.caption}` : '',
    url: photo.route.url,
    image: {
      url: `${photo.photoSizes[largestIndex].url}`,
      width: photo.photoSizes[largestIndex].width,
      height: photo.photoSizes[largestIndex].height,
    },
  };
};

const isLandscape = photo => {
  if (photo?.photoSizes == null || photo?.photoSizes.length === 0) {
    return false;
  }

  const last = photo.photoSizes.length - 1;
  const lastPhoto = photo.photoSizes[last];

  return lastPhoto.width / lastPhoto.height >= 1 + THRESHOLD;
};

const isSquare = photo => {
  if (photo?.photoSizes == null || photo?.photoSizes.length === 0) {
    return false;
  }

  const last = photo.photoSizes.length - 1;
  const lastPhoto = photo.photoSizes[last];

  return Math.abs(1 - lastPhoto.width / lastPhoto.height) <= THRESHOLD;
};

const isPortrait = photo => !isSquare(photo) && !isLandscape(photo);

const isAllLandscape = photos => photos.every(isLandscape);
const isAllPortrait = photos => photos.every(isPortrait);
const isAllSquare = photos => photos.every(isSquare);

export const getCarouselHeight = (photos: Array<PhotoWithMetaData>, containerWidth: number) => {
  // if the photos are all landscape, the carousel should be 4:3
  // if the photos are all portrait, the carousel should be 3:4,
  // otherwise they are all square

  if (isAllSquare(photos)) {
    // noinspection JSSuspiciousNameCombination
    return containerWidth;
  } else if (isAllLandscape(photos)) {
    return (containerWidth / 4) * 3;
  } else if (isAllPortrait(photos)) {
    return (containerWidth / 3) * 4;
  }
  // noinspection JSSuspiciousNameCombination
  return containerWidth;
};

const FeedSectionPhotoCarousel = (props: Props) => {
  const carouselModalName = `${props.metadata.__typename}_${props.metadata.id}`;
  // Routed modals are really subpages and belong in its own application state.
  // Right now, each section can render the many modals of the same type.
  // This is because each section is listening in on the url, and it does a naive pattern match to see whether or not the modal is displayed
  // This results in subtle bugs; mainly that the modal you see on the screen does not correspond to the section you clicked on
  // Unfortunately, this can't be enforced cleanly at the component-level given SEO requirements. As a stop-gap, track when a feed section carousel opens locally to make sure we always show the right one and maintain that state
  // Also make sure to to derive initial local state from route state for deep-linking
  const { matches, to, close } = useRouteState({ modal: carouselModalName });
  const [isLocallyOpen, setOpen] = React.useState<boolean>(matches);
  // A best guess of the container width on initial render as photo dimensions cannot be computed before it is rendered
  const [containerWidth, setContainerWidth] = React.useState<number>(
    props.viewportCategory === 'MOBILE' ? 411 : 600,
  );

  const containerRef = React.useRef(null);
  React.useEffect(() => {
    if (containerRef.current && typeof containerRef.current.getBoundingClientRect === 'function') {
      const newWidth = containerRef.current.getBoundingClientRect().width;
      setContainerWidth(newWidth);
    } else {
      Logging.error('Container ref is not properly set in FeedSectionPhotoCarousel');
    }
  }, []);

  const onModalClose = () => {
    close();
    setOpen(false);
  };

  if (!props.photos || props.photos.length === 0) {
    return null;
  }
  return (
    <RouteConsumer>
      {({ route }) => (
        <div ref={containerRef} className={styles.carouselBottom}>
          {route?.modal === carouselModalName ? (
            <HeadProperties openGraphInfo={getOpenGraphInfoFromPhoto(props.photos[0])} />
          ) : null}
          {props.includeRoutedModal && matches && isLocallyOpen && (
            <MountedClientOnly>
              <ModalView
                header={
                  <SectionModalHeader>
                    <BrandedHeader branding="TA_GREEN" />
                  </SectionModalHeader>
                }
                onClose={onModalClose}
                closeXColorVariant="WHITE"
                isFullBleed
                bodyScroll={false}
                fullscreenAlways
              >
                <SectionModal
                  images={props.photos}
                  actor={props.actor}
                  deleteObject={props.deleteObject}
                  untagObject={props.untagObject}
                  showPOISavesIcon
                  currentIndex={props.currentlyDisplayedIndex}
                  onPhotoChange={props.onPhotoChange}
                  viewportCategory={props.viewportCategory}
                  additionalContent={props.additionalModalContent}
                  socialStatisticsBar={props.socialStatisticsBar}
                  socialActionBar={props.socialActionBar}
                  overflowMenuObject={props.overflowMenuObject}
                  overflowMenuObjectForType={props.overflowMenuObjectForType}
                />
              </ModalView>
            </MountedClientOnly>
          )}
          <Link
            to={{
              ...to,
              username: props?.actorForUrl?.username || props?.actor?.username,
            }}
            href={null} // this needs to be null - non canonical urls cannot be present as href
            onClick={e => {
              setOpen(true);
              if (props.onCarouselClick) {
                props.onCarouselClick(e);
              }
            }}
            activeClassName=""
            className={styles.link}
          >
            <PhotoCarousel
              photos={props.photos.map(photo => ({
                id: photo.id,
                photoSizes: photo.photoSizes,
              }))}
              width="100%" // width has to be 100% because it needs to fill the width of the feed
              height={`${getCarouselHeight(props.photos, containerWidth)}px`} // height needs to be either 4:3 or 3:4 or equal the width
              displayIndex={props.currentlyDisplayedIndex}
              totalCount={props.photos.length}
              showIndicatorDots={props.photos.length > 1}
              showProgress={props.photos.length > 1}
              hideArrows
              noCta
              onPhotoChange={props.onPhotoChange}
              maxIndicatorDots={10}
              lazyLoadImages
            />
          </Link>
        </div>
      )}
    </RouteConsumer>
  );
};

export default FeedSectionPhotoCarousel;
